import { Play, DollarSign, Repeat } from 'lucide-react'
import { useConfigStore } from '../store/configStore'
import { useEditorStore } from '../store/editorStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { useState } from 'react'

export function AnimationEditor() {
  const { templateConfig } = useConfigStore();
  const {
    currentAnimation,
    setCurrentAnimation,
    updateAnimationPro,
    updateAnimationLoop,
  } = useEditorStore();

  const [editingPro, setEditingPro] = useState<string | null>(null);
  const [proValue, setProValue] = useState('');

  const handleProEdit = (animId: string, currentPro: number) => {
    setEditingPro(animId);
    setProValue(currentPro.toString());
  };

  const handleProSave = () => {
    if (editingPro) {
      const pro = parseInt(proValue) || 0;
      updateAnimationPro(editingPro, Math.max(0, pro));
      setEditingPro(null);
    }
  };

  const handleProKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleProSave();
    } else if (e.key === 'Escape') {
      setEditingPro(null);
    }
  };

  if (!templateConfig) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
        暂无数据
      </div>
    );
  }

  const animations = templateConfig.animations || [];

  if (animations.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
        未找到动画
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-280px)]">
      <div className="space-y-2 pr-4">
        {animations.map((anim) => {
          const isPlaying = currentAnimation === anim.name;
          const isEditing = editingPro === anim.id;

          return (
            <div
              key={anim.id}
              className={cn(
                "p-4 rounded-lg border transition-all",
                isPlaying 
                  ? "bg-primary/10 border-primary/50 shadow-sm" 
                  : "bg-card hover:bg-muted/50"
              )}
            >
              <div className="flex items-start gap-3">
                {/* 播放按钮 */}
                <Button
                  variant={isPlaying ? "default" : "outline"}
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  onClick={() => setCurrentAnimation(anim.name)}
                >
                  <Play className={cn("h-4 w-4", isPlaying && "fill-current")} />
                </Button>

                {/* 动画信息 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-sm truncate">{anim.name}</h4>
                    {anim.pro > 0 && (
                      <Badge variant="secondary">¥{anim.pro}</Badge>
                    )}
                    {anim.loop && (
                      <Repeat className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                    ID: {anim.id}
                  </p>

                  {/* 配置区域 */}
                  <div className="flex items-center gap-4 mt-3">
                    {/* 循环开关 */}
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`loop-${anim.id}`} className="text-xs">
                        循环
                      </Label>
                      <Switch
                        id={`loop-${anim.id}`}
                        checked={anim.loop}
                        onCheckedChange={(checked) => updateAnimationLoop(anim.id, checked)}
                      />
                    </div>

                    {/* 价格编辑 */}
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">价格</Label>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={proValue}
                          onChange={(e) => setProValue(e.target.value)}
                          onBlur={handleProSave}
                          onKeyDown={handleProKeyDown}
                          className="w-20 h-7 text-sm"
                          autoFocus
                        />
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 gap-1"
                          onClick={() => handleProEdit(anim.id, anim.pro)}
                        >
                          <DollarSign className="h-3 w-3" />
                          <span>{anim.pro}</span>
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* 快速设置价格 */}
                  <div className="flex gap-1 mt-2">
                    {[0, 100, 500, 1000].map((price) => (
                      <Button
                        key={price}
                        variant={anim.pro === price ? "default" : "outline"}
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => updateAnimationPro(anim.id, price)}
                      >
                        {price === 0 ? '免费' : `¥${price}`}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}

